<script lang="ts">
import type { SetupContext } from 'vue';
import type { HekateDisplayField } from '@onshape/www-inertia';
import CoordinatorProvider from '../coordinator/CoordinatorProvider';
import FieldErrorMessage from './FieldErrorMessage.vue';

export default defineComponent({
  components: {
    FieldErrorMessage,
  },
  props: {
    field: {
      required: true,
      type: Object as () => HekateDisplayField,
    },
    modelValue: {
      required: true,
      type: String,
    },
  },
  setup(props: { modelValue: string; field: HekateDisplayField }, context: SetupContext) {
    const { field, modelValue } = toRefs(props);
    return CoordinatorProvider.get().autoCompleteField({
      field,
      modelValue,
      context,
    });
  },
});
</script>

<template>
  <div
    :class="{
      'auto-complete-container': true,
      'field-container': true,
      'field-content': true,
      'field-content-is-validated': field.isValidated,
      'field-content-is-errored': field.errorMessages.length > 0,
    }"
  >
    <label :for="field.htmlName">
      <span>{{ $t(field.name, field.htmlName, field.label) }}</span
      ><sup
        v-if="field.isRequired"
        class="required-indicator"
        title="This field is required"
        >*</sup
      >
    </label>
    <div class="d-flex align-items-center field-auto-complete auto-complete-wrapper">
      <input
        :id="field.htmlName"
        v-model="query"
        :type="inputType"
        :placeholder="placeholder"
        :value="query"
        :html-name="field.htmlName"
        :required="field.isRequired"
        :disabled="field.isDisabled"
        @input="filterOptions"
        @focus="onFocus"
        @keydown.down.prevent="onArrowDown"
        @keydown.up.prevent="onArrowUp"
        @keydown.enter.prevent="onEnter"
      />
      <img
        v-show="!showElement"
        class="search-icon"
        :src="searchImageSrc"
        alt=""
      />
      <img
        v-show="showElement"
        class="search-icon"
        :src="closeImageSrc"
        alt=""
        @click="clearQuery"
        @keydown.enter="clearQuery"
      />
    </div>

    <field-error-message :messages="field.errorMessages" />

    <div
      v-show="showElement"
      class="search-result"
    >
      <ul v-if="filteredOptions.length && query.length">
        <span class="suggested-title">{{ suggested }}</span>
        <li
          v-for="(option, index) in filteredOptions"
          :key="option"
          tabindex="0"
          :class="{ 'is-active': index === activeIndex }"
          @keydown.enter="() => selectOption(option, index)"
          @click="() => selectOption(option, index)"
        >
          <span style="font-weight: bold">{{ highlightedOption(option)[0] }}</span>
          <span>{{ highlightedOption(option)[1] }}</span>
        </li>
      </ul>
      <span
        v-show="filteredOptions.length === 0 && query.length > 0"
        class="suggested-title no-result"
        >{{ noSuggestions }}</span
      >
      <div class="searched-text">
        <span v-show="query.length > 0">{{ query }}</span>
        <span
          v-show="query.length == 0"
          class="start-type"
          >{{ startTyping }}</span
        >
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '../../../style/vars.scss' as vars;
.auto-complete-container {
  margin-bottom: 12px;
  label span {
    font-size: 17px;
  }
}

#hekate-form-fort-container {
  form .auto-complete-wrapper {
    border: 1px solid #dbe0e0;
    background: #fafafa;
    padding-right: 22px;
    position: relative;
    input {
      border: none;
      width: 100%;
      margin-bottom: 0px;
      outline: none;
      text-align: left;
      &::placeholder {
        text-align: left;
      }
    }
  }
  .field-content {
    width: 100%;
    &.field-content-is-errored {
      .auto-complete-wrapper {
        border: 1px solid vars.$color-error;
      }
    }
  }

  .field-content-is-validated {
    .auto-complete-wrapper {
      border-right: 4px solid vars.$color-onshape-green;
    }
  }
}
.search-icon {
  cursor: pointer;
  width: 12px;
  height: 14px;
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
}
.start-type {
  font-family: Rajdhani;
  font-size: 12px;
  font-weight: 700;
  line-height: 15.31px;
  text-align: left;
  color: #919fa1;
}
.suggested-title {
  font-family: Rajdhani;
  font-size: 12px;
  font-weight: 700;
  line-height: 15.31px;
  text-align: left;
  padding: 0px 14px;
}
.search-result {
  border: 1px solid #dbe0e0;
  box-shadow: 0px 0px 10px 0px #00000026;
  margin: 0px 0 12px;
}
.search-result ul {
  margin: 0px;
  padding: 10px 0;
  list-style: none;
}
li {
  cursor: pointer;
  padding: 0px 14px;
}
li.is-active {
  background: #649ff9;
  color: white;
}
li:hover {
  background-color: #ddd;
}
.no-result {
  padding: 10px 14px;
}
.searched-text {
  border-top: 1px solid #dbe0e0;
  padding: 10px 14px;
}
.field-text-input {
  width: 100%;

  ::placeholder {
    text-align: center;
  }
}
.field-content:has([for^='addEmail']) {
  width: 90%;
  margin-left: auto;
}

.field-content {
  width: 100%;
  &.field-content-is-errored {
    .auto-complete-wrapper {
      border: 1px solid vars.$color-error;
    }
  }
}

.field-content-is-validated {
  .auto-complete-wrapper {
    border-right: 4px solid vars.$color-onshape-green;
  }
}
</style>
